/**
 * Parse Locations workspace hash: `#location-N` or `#location-N/variant-V`.
 *
 * Scene phone links are path segments on Scenes3d (`/view`, `/fly`), not hashes.
 */
export type LocationSceneHash = {
  locationId: number
  variantId?: number
}

/** `#scene3d-12` on Scenes3d: open that scene, do not start phone fly. */
export function parseScene3dWorkspaceSelectHash(
  hash: string = typeof window !== "undefined" ? window.location.hash : "",
): { scene3dId: number } | null {
  const m = hash.match(/^#scene3d-(\d+)$/)
  if (!m) return null
  const scene3dId = Number(m[1])
  if (!Number.isFinite(scene3dId) || scene3dId <= 0) return null
  return { scene3dId }
}

/** Scene id from `/workspace/scenes_3d/215`, `/215/view`, or `/215/fly`. Bare `/scenes_3d` is empty. */
export function readScene3dIdFromPath(
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): number | null {
  const match = pathname.match(/\/workspace\/scenes_3d\/(\d+)(?:\/(?:view|fly)(?:\/shot\/\d+)?)?$/)
  if (!match) return null
  const scene3dId = Number(match[1])
  if (!Number.isFinite(scene3dId) || scene3dId <= 0) return null
  return scene3dId
}

/** `/workspace/scenes_3d/215/view` or `/215/view/shot/9`. */
export function readScene3dViewFromPath(
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): { scene3dId: number; shotId?: number } | null {
  const match = pathname.match(/\/workspace\/scenes_3d\/(\d+)\/view(?:\/shot\/(\d+))?$/)
  if (!match) return null
  const scene3dId = Number(match[1])
  if (!Number.isFinite(scene3dId) || scene3dId <= 0) return null
  const shotRaw = match[2] ? Number(match[2]) : undefined
  const shotId = shotRaw != null && Number.isFinite(shotRaw) && shotRaw > 0 ? shotRaw : undefined
  return { scene3dId, shotId }
}

/** `/workspace/scenes_3d/215/fly` or `/215/fly/shot/9`. */
export function readScene3dFlyFromPath(
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): { scene3dId: number; shotId?: number } | null {
  const match = pathname.match(/\/workspace\/scenes_3d\/(\d+)\/fly(?:\/shot\/(\d+))?$/)
  if (!match) return null
  const scene3dId = Number(match[1])
  if (!Number.isFinite(scene3dId) || scene3dId <= 0) return null
  const shotRaw = match[2] ? Number(match[2]) : undefined
  const shotId = shotRaw != null && Number.isFinite(shotRaw) && shotRaw > 0 ? shotRaw : undefined
  return { scene3dId, shotId }
}

/** Path of one scene on the 3D scenes page. */
export function scene3dWorkspacePath(projectId: number, scene3dId: number): string {
  return `/projects/${projectId}/workspace/scenes_3d/${scene3dId}`
}

/** `#view` or `#view/shot-9` on Scenes3d. Scene id comes from the path. */
export function parseScene3dWorkspaceViewHash(
  hash: string = typeof window !== "undefined" ? window.location.hash : "",
): { shotId?: number } | null {
  const match = hash.match(/^#view(?:\/shot-(\d+))?$/)
  if (!match) return null
  const shotRaw = match[1] ? Number(match[1]) : undefined
  const shotId = shotRaw != null && Number.isFinite(shotRaw) && shotRaw > 0 ? shotRaw : undefined
  return { shotId }
}

export function parseLocationSceneHash(
  hash: string = typeof window !== "undefined" ? window.location.hash : "",
): LocationSceneHash | null {
  const m = hash.match(/^#location-(\d+)(?:\/variant-(\d+))?$/)
  if (!m) return null
  const locationId = Number(m[1])
  if (!Number.isFinite(locationId) || locationId <= 0) return null
  const variantRaw = m[2] ? Number(m[2]) : undefined
  const variantId = variantRaw != null && Number.isFinite(variantRaw) && variantRaw > 0 ? variantRaw : undefined
  return { locationId, variantId }
}

/** Location id only, whatever the suffix. */
export function parseLocationHash(hash?: string): number | null {
  const parsed = parseLocationSceneHash(hash)
  return parsed?.locationId ?? null
}

export function locationHash(locationId: number): string {
  return `#location-${locationId}`
}

function absolutePath(path: string): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${path}`
  }
  return path
}

/** QR for the mobile camera view. Mode is a path segment so a phone camera keeps it. */
export function scene3dWorkspaceViewUrl(
  projectId: number,
  scene3dId: number,
  opts?: { shotId?: number | null },
): string {
  let path = `/projects/${projectId}/workspace/scenes_3d/${scene3dId}/view`
  if (opts?.shotId != null && opts.shotId > 0) path += `/shot/${opts.shotId}`
  return absolutePath(path)
}

/** QR for phone fly. Mode is a path segment so a phone camera keeps it. */
export function scene3dWorkspaceFlyUrl(
  projectId: number,
  scene3dId: number,
  opts?: { shotId?: number | null },
): string {
  let path = `/projects/${projectId}/workspace/scenes_3d/${scene3dId}/fly`
  if (opts?.shotId != null && opts.shotId > 0) path += `/shot/${opts.shotId}`
  return absolutePath(path)
}
